import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, Upload, Loader2, Download, ArrowRight, FileText, CheckCircle2, Trash2, Folder } from "lucide-react";

/**
 * DesignWorkItem — the ONLY page 2D/3D Design ever sees for a lead.
 * Renders backend-redacted enquiry response: project name + customer name +
 * requirements + design_brief (notes + files from Sales Coord) + upload area
 * for the designer's drawings + a "Send drawings back to Sales Coord" action.
 * No phone / email / address / free-text notes / timeline are exposed.
 */
export default function DesignWorkItem() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [e, setE] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState("");
  const [folder, setFolder] = useState("");

  const fetchIt = useCallback(async () => {
    try {
      const { data } = await api.get(`/enquiry/${id}`);
      setE(data);
    } catch (err) { setError(formatApiError(err)); }
  }, [id]);

  useEffect(() => { fetchIt(); }, [fetchIt]);

  if (error) return <DesktopLayout><div className="text-red-600">{error}</div></DesktopLayout>;
  if (!e) return <DesktopLayout><div className="text-slate-500">Loading…</div></DesktopLayout>;

  const brief = e.design_brief || {};
  const attached = e.files || [];
  const briefFileIds = new Set(brief.attached_file_ids || []);
  const briefFiles = attached.filter((f) => briefFileIds.has(f.id));
  const myUploads = attached.filter((f) => !briefFileIds.has(f.id));

  const uploadDrawings = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const form = new FormData();
        form.append("file", f);
        if (folder.trim()) form.append("folder", folder.trim());
        await api.post(`/enquiry/${e.id}/attach`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(folder.trim() ? `Uploaded to "${folder.trim()}"` : "Drawings uploaded");
      await fetchIt();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const sendBack = async () => {
    setSending(true);
    try {
      await api.post(`/enquiry/${e.id}/drawings-ready`, { note: note.trim() });
      toast.success("Drawings sent back to Sales Coordination");
      navigate("/dashboard");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  const removeUpload = async (fileId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"? This can't be undone.`)) return;
    try {
      await api.delete(`/enquiry/${e.id}/files/${fileId}`);
      toast.success("File removed");
      await fetchIt();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DesktopLayout>
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-900 mb-4">
        <ChevronLeft className="w-3.5 h-3.5" /> All projects
      </Link>

      {/* Header — only project name + customer name */}
      <div className="mb-6" data-testid="design-workitem-header">
        <div className="hk-tiny-label" style={{ color: "#E52B50" }}>2D / 3D Design workitem</div>
        <h1 className="text-4xl font-black tracking-tighter mb-1" style={{ fontFamily: "Chivo" }} data-testid="design-project-name">
          {e.name}
        </h1>
        <div className="text-sm text-slate-600">
          <span className="hk-tiny-label mr-2">Customer</span>
          <span className="font-bold" data-testid="design-customer-name">{e.name}</span>
          <span className="ml-3 font-mono text-xs text-slate-500">{e.enquiry_id}</span>
        </div>
        {e.requirements && e.requirements.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {e.requirements.map((r) => (
              <span key={r} className="text-[10px] font-black uppercase px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700">{r}</span>
            ))}
          </div>
        )}
        {e.drawings_reject_reason && (
          <div className="mt-3 border-l-4 border-red-500 bg-red-50 p-3 rounded-sm" data-testid="design-rework-banner">
            <div className="hk-tiny-label text-red-700 mb-1">Rework requested by Sales</div>
            <div className="text-sm text-red-800 whitespace-pre-wrap">{e.drawings_reject_reason}</div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Design Brief from Sales Coord */}
        <section className="hk-card p-5" data-testid="design-brief-card">
          <div className="hk-tiny-label mb-3 flex items-center gap-1">
            <FileText className="w-3 h-3" /> Notes from Sales Coordination
          </div>
          {brief.notes ? (
            <div className="whitespace-pre-wrap text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-sm p-3 mb-3" data-testid="design-brief-notes">
              {brief.notes}
            </div>
          ) : (
            <div className="text-sm text-slate-400 italic mb-3">No notes attached.</div>
          )}
          {brief.sent_by_name && (
            <div className="text-[11px] text-slate-500 mb-4">
              Sent by <b>{brief.sent_by_name}</b>
              {brief.sent_at && <> · {new Date(brief.sent_at).toLocaleString()}</>}
            </div>
          )}
          <div className="hk-tiny-label mb-2">Attached files ({briefFiles.length})</div>
          {briefFiles.length === 0 ? (
            <div className="text-xs text-slate-400 italic">No files attached by Sales Coord.</div>
          ) : (
            <ul className="space-y-1.5">
              {briefFiles.map((f) => (
                <li key={f.id} data-testid={`design-brief-file-${f.id}`}>
                  <a href={`${api.defaults.baseURL}/enquiry/${e.id}/files/${f.id}`} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-2 text-xs text-slate-700 hover:text-orange-700 border border-slate-200 hover:border-orange-400 rounded-sm px-2 py-1.5">
                    <Download className="w-3 h-3" />
                    <span className="truncate flex-1">{f.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Upload area for designer's own drawings */}
        <section className="hk-card p-5" data-testid="design-upload-card">
          <div className="hk-tiny-label mb-3 flex items-center gap-1">
            <Upload className="w-3 h-3" /> Upload your drawings
          </div>
          {/* Optional folder to organise this upload batch */}
          <div className="mb-2">
            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-1">
              <Folder className="w-3 h-3" /> Folder <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <input type="text" value={folder} onChange={(ev) => setFolder(ev.target.value)}
              placeholder="e.g. Rev-1 · Elevations · 3D views"
              maxLength={60}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white"
              data-testid="design-upload-folder" />
            <div className="text-[10px] text-slate-400 mt-1">
              Leave blank to upload to the enquiry root. Files uploaded together share this folder name.
            </div>
          </div>
          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-sm p-6 cursor-pointer hover:border-orange-500 text-sm font-semibold" data-testid="design-upload-input">
            <input type="file" hidden multiple onChange={(ev) => uploadDrawings(ev.target.files)} />
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <>Choose drawing file(s)</>}
          </label>
          <div className="hk-tiny-label mt-4 mb-2">Uploaded by you ({myUploads.length})</div>
          {myUploads.length === 0 ? (
            <div className="text-xs text-slate-400 italic">No drawings uploaded yet.</div>
          ) : (
            <MyUploadsList uploads={myUploads} onRemove={removeUpload} />
          )}

          <div className="mt-5 pt-4 border-t border-slate-200">
            <div className="hk-tiny-label mb-2">Note to Sales Coordination (optional)</div>
            <textarea rows={2} value={note} onChange={(ev) => setNote(ev.target.value)}
              placeholder="e.g. Rev 1 attached — please verify island dimensions"
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white"
              data-testid="design-return-note" />
            <button disabled={sending || myUploads.length === 0} onClick={sendBack}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-black uppercase text-xs px-4 py-3 rounded-sm"
              data-testid="design-send-back">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
              Send drawings to Sales Coord
            </button>
            {myUploads.length === 0 && (
              <div className="text-[10px] text-slate-400 italic mt-1">Upload at least one drawing to send back.</div>
            )}
          </div>
        </section>
      </div>
    </DesktopLayout>
  );
}

// Renders the designer's own uploads grouped by folder. Root (unfoldered)
// files show up first, followed by named folders in alphabetical order.
function MyUploadsList({ uploads, onRemove }) {
  const groups = React.useMemo(() => {
    const out = {};
    for (const f of uploads || []) {
      const k = (f.folder || "").trim() || "";
      if (!out[k]) out[k] = [];
      out[k].push(f);
    }
    return Object.entries(out).sort(([a], [b]) => {
      if (a === "" && b !== "") return -1;
      if (b === "" && a !== "") return 1;
      return a.localeCompare(b);
    });
  }, [uploads]);
  return (
    <div className="space-y-3" data-testid="design-my-uploads">
      {groups.map(([folder, items]) => (
        <div key={folder || "__root"}>
          {folder && (
            <div className="flex items-center gap-1 text-[10px] uppercase font-bold text-slate-500 mb-1">
              <Folder className="w-3 h-3" /> {folder}
              <span className="text-slate-400 font-normal">({items.length})</span>
            </div>
          )}
          <ul className="space-y-1.5">
            {items.map((f) => (
              <li key={f.id} data-testid={`design-upload-${f.id}`}
                className="flex items-center gap-2 text-xs border border-emerald-200 bg-emerald-50 rounded-sm px-2 py-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                <span className="truncate flex-1">{f.name}</span>
                <button type="button" onClick={() => onRemove(f.id, f.name)}
                  title="Delete this upload"
                  className="text-slate-400 hover:text-rose-600 shrink-0"
                  data-testid={`design-upload-delete-${f.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
